import { useState } from 'react';
import { RcFile } from 'antd/es/upload';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useNotificationContext } from '@/features/notifications';
import {
  OpenapiContentType,
  OrganizationMembershipResponse,
  useGetOrganizationMemberships,
  useUpdateOrganization,
  useUploadFileToOrganization,
} from '@/shared/api';
import { useDataInvalidation } from '@/shared/hooks';
import { useRouterContext } from '@/shared/router';
import { Col } from '@/shared/ui';
import { PageTitleBanner } from '@/shared/ui/page-title-banner';
import { captureError } from '@/shared/utils/error-reporting';
import { PageLayout, PageLayoutHeaderGoBackBtn } from '@/widgets/page-layout';
import { OrganizationTabs } from './OrganizationTabs';

export const OrganizationPage = () => {
  const invalidate = useDataInvalidation();
  const navigate = useNavigate();
  const { activeOrganization, setActiveOrganization, dashboardGoBackLink } =
    useRouterContext();
  const { id } = activeOrganization as OrganizationMembershipResponse;
  const { showNotification } = useNotificationContext();
  const [isEditMode, setIsEditMode] = useState(false);

  const {
    data: organizationData,
    isLoading: organizationDataLoading,
    refetch: refetchOrganizationData,
  } = useGetOrganizationMemberships<OrganizationMembershipResponse>(
    { pathParams: { id } },
    { enabled: Boolean(activeOrganization?.id) },
  );

  const { mutate: updateOrganization, isLoading: updateOrganizationLoading } =
    useUpdateOrganization();
  const { mutateAsync: uploadImage, isLoading: isUploading } =
    useUploadFileToOrganization();

  const handleOrganizationNameChange = (newOrgName: string) => {
    updateOrganization(
      {
        pathParams: { id },
        body: { name: newOrgName },
      },
      {
        onSuccess: () => {
          showNotification({
            type: 'success',
            message: 'Organization name successfully changed',
            description: '',
          });
          invalidate(['accounts', 'my-profile']);
          invalidate(['organizations', activeOrganization?.id]);
          setActiveOrganization({
            ...(activeOrganization as OrganizationMembershipResponse),
            name: newOrgName,
          });
          setIsEditMode(false);
        },
        onError: (error) => {
          showNotification({
            type: 'error',
            message: 'Update error',
            description: error.message,
          });

          captureError(error, {
            module: 'organization_page_on_error_update_organization',
            action: 'update_organization',
          });
        },
      },
    );
  };

  const beforeUpload = async (file: RcFile) => {
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
    if (!isJpgOrPng) {
      showNotification({
        type: 'error',
        message: 'Error uploading image',
        description: 'You can only upload JPG/PNG file!',
      });
    }
    const isLt1M = file.size / 1024 / 1024 < 0.51;
    if (!isLt1M) {
      showNotification({
        type: 'error',
        message: 'Error uploading image',
        description: 'Image must be smaller than 500KB!',
      });
    }
    if (isLt1M && isJpgOrPng) {
      const res = await uploadImage({
        contentType: OpenapiContentType.FormData,
        pathParams: { organizationId: id },
        body: { file },
      });

      if (res?.fileKey && res?.url) {
        updateOrganization(
          {
            pathParams: { id },
            body: { uploadedFileKey: res.fileKey },
          },
          { onSuccess: () => refetchOrganizationData() },
        );
      }
    }
    return false;
  };

  const handleImageRemove = () => {
    updateOrganization(
      { pathParams: { id }, body: { uploadedFileKey: '' } },
      {
        onSuccess: () => refetchOrganizationData(),
        onError: (error) =>
          captureError(error, {
            module: 'organization_page_on_error_remove_image',
            action: 'remove_image',
          }),
      },
    );
  };

  if (activeOrganization && activeOrganization.role !== 'admin') {
    navigate('/dashboard');
    return <div>Redirecting...</div>;
  }

  return (
    <PageLayout
      headerContent={
        <HeaderWrapper>
          <PageLayoutHeaderGoBackBtn
            path={dashboardGoBackLink}
            label="Go back to dashboard"
          />
        </HeaderWrapper>
      }
      contentStyle={{ padding: 0, paddingTop: 0 }}
    >
      <PageTitleBanner
        canEdit
        title={organizationData?.name}
        imageUrl={organizationData?.uploadedImageLink}
        bannerColor={organizationData?.theme?.bannerColor}
        isEditMode={isEditMode}
        isLoading={
          isUploading || organizationDataLoading || updateOrganizationLoading
        }
        onAvatarUpload={beforeUpload}
        onAvatarRemove={handleImageRemove}
        onTitleUpdate={handleOrganizationNameChange}
        onToggleMode={setIsEditMode}
        contentStyle={{ maxWidth: '1024px', margin: 'auto' }}
      />
      <Wrapper>
        <OrganizationTabs />
      </Wrapper>
    </PageLayout>
  );
};

const HeaderWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Wrapper = styled(Col)`
  width: 100%;
  padding: 10px 30px;
  max-width: 1024px;
  margin: 0 auto;
`;
